import React, { useEffect, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import Stack from "@mui/joy/Stack";
import Input from "@mui/joy/Input";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import {
  Button,
  IconButton,
  CircularProgress,
  Autocomplete,
  Typography,
} from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  validateEntityName,
  reindexErrors,
} from "../../../utils/validateEntityName";
import { buildAttendanceSubmission } from "./attendanceSubmission";
import type {
  AttendanceEntry,
  ManualAttendanceSubmission,
} from "./attendanceSubmission";

interface AddEventModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: ManualAttendanceSubmission) => Promise<void> | void;
  eventOptions: string[];
}

const AddEventModal: React.FC<AddEventModalProps> = ({
  open,
  onClose,
  onAdd,
  eventOptions,
}) => {
  const [entries, setEntries] = useState<AttendanceEntry[]>([
    { company: "", person: "" },
  ]);
  const [eventName, setEventName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [eventNameError, setEventNameError] = useState<string | null>(null);
  const [companyErrors, setCompanyErrors] = useState<Record<number, string>>(
    {},
  );
  const [personErrors, setPersonErrors] = useState<Record<number, string>>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntries([{ company: "", person: "" }]);
      setEventName("");
      setLoading(false);
      setEventNameError(null);
      setCompanyErrors({});
      setPersonErrors({});
      setSubmissionError(null);
      setSubmitAttempted(false);
    }
  }, [open]);

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    index: number,
    field: "company" | "person",
  ) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const splitRows = pastedText.split(/\r?\n/).map((row) => row.split("\t"));

    setEntries((prev) => {
      const newEntries = [...prev];

      splitRows.forEach((row, i) => {
        const [company = "", person = ""] = row;
        const targetIndex = index + i;

        if (!newEntries[targetIndex]) {
          newEntries[targetIndex] = { company: "", person: "" };
        }

        if (field === "company") {
          // Pasting into the company field -> update both if two columns exist
          newEntries[targetIndex] = {
            company: company,
            person: person || newEntries[targetIndex].person,
          };
        } else {
          // Pasting into the person field -> only update the person column
          newEntries[targetIndex].person = company; // Since only one column is copied, assume it's a person name
        }
      });

      return newEntries;
    });
  };

  const handleInputChange = (
    index: number,
    field: "company" | "person",
    value: string,
  ) => {
    if (submitAttempted) {
      const label = field === "company" ? "Company name" : "Person name";
      const error = value.trim() ? validateEntityName(value, label) : null;
      const setter = field === "company" ? setCompanyErrors : setPersonErrors;
      setter((prev) => {
        const next = { ...prev };
        if (error) next[index] = error;
        else delete next[index];
        return next;
      });
    }

    setEntries((prev) => {
      const newEntries = [...prev];
      newEntries[index] = { ...newEntries[index], [field]: value };

      if (index === newEntries.length - 1 && value.trim() !== "") {
        newEntries.push({ company: "", person: "" });
      }

      return newEntries;
    });
  };

  const handleDelete = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
    setCompanyErrors((prev) => reindexErrors(prev, index));
    setPersonErrors((prev) => reindexErrors(prev, index));
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    const nameErr = validateEntityName(eventName, "Conference name");
    if (nameErr) {
      setEventNameError(nameErr);
      return;
    }

    const newCompanyErrors: Record<number, string> = {};
    const newPersonErrors: Record<number, string> = {};
    entries.forEach((entry, idx) => {
      if (entry.person.trim() && !entry.company.trim()) {
        newCompanyErrors[idx] =
          "Company name is required when an attendee is provided";
      }
      if (entry.company.trim()) {
        const err = validateEntityName(entry.company, "Company name");
        if (err) newCompanyErrors[idx] = err;
      }
      if (entry.person.trim()) {
        const err = validateEntityName(entry.person, "Person name");
        if (err) newPersonErrors[idx] = err;
      }
    });
    if (
      Object.keys(newCompanyErrors).length > 0 ||
      Object.keys(newPersonErrors).length > 0
    ) {
      setCompanyErrors(newCompanyErrors);
      setPersonErrors(newPersonErrors);
      return;
    }

    setLoading(true);
    setSubmissionError(null);
    const filteredEntries = entries.filter(
      (e) => e.company.trim() || e.person.trim(),
    );
    const failedEntries: AttendanceEntry[] = [];

    for (const { company, person } of filteredEntries) {
      const entry = { company, person };
      const data = buildAttendanceSubmission(entry, eventName);

      try {
        await onAdd(data);
      } catch (error) {
        failedEntries.push(entry);
        console.error(`Failed to upload entry: ${company} - ${person}`, error);
      }
    }
    setLoading(false);
    if (failedEntries.length > 0) {
      setEntries([...failedEntries, { company: "", person: "" }]);
      setSubmissionError(
        `${failedEntries.length} attendance row(s) failed to upload. ` +
          "Successful rows were removed; review and retry the remaining rows.",
      );
      return;
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        className="modal"
        sx={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <DialogTitle>Add Conference Attendance</DialogTitle>
        <Typography level="body-sm" color="neutral">
          Record which companies and people attended a conference. You can paste
          multiple rows from a spreadsheet.
        </Typography>
        {submissionError && (
          <Typography level="body-sm" color="danger">
            {submissionError}
          </Typography>
        )}
        <Stack spacing={2}>
          <FormControl error={!!eventNameError}>
            <Autocomplete
              freeSolo
              options={eventOptions}
              value={eventName}
              color={eventNameError ? "danger" : "neutral"}
              onInputChange={(_, newValue) => {
                setEventName(newValue || "");
                if (submitAttempted) {
                  setEventNameError(
                    validateEntityName(newValue || "", "Conference name"),
                  );
                }
              }}
              placeholder="Conference name (enter or select from suggestions)"
            />
            {eventNameError && (
              <FormHelperText>{eventNameError}</FormHelperText>
            )}
          </FormControl>
          {entries.map((entry, idx) => (
            <Stack key={idx} spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <FormControl error={!!companyErrors[idx]} sx={{ flexGrow: 1 }}>
                  <Input
                    placeholder="Company name"
                    value={entry.company}
                    color={companyErrors[idx] ? "danger" : "neutral"}
                    onChange={(e) =>
                      handleInputChange(idx, "company", e.target.value)
                    }
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                      handlePaste(e, idx, "company")
                    }
                  />
                  {companyErrors[idx] && (
                    <FormHelperText>{companyErrors[idx]}</FormHelperText>
                  )}
                </FormControl>
                <FormControl error={!!personErrors[idx]} sx={{ flexGrow: 1 }}>
                  <Input
                    placeholder="Attendee name"
                    value={entry.person}
                    color={personErrors[idx] ? "danger" : "neutral"}
                    onChange={(e) =>
                      handleInputChange(idx, "person", e.target.value)
                    }
                    onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                      handlePaste(e, idx, "person")
                    }
                  />
                  {personErrors[idx] && (
                    <FormHelperText>{personErrors[idx]}</FormHelperText>
                  )}
                </FormControl>
                {(entry.company.trim() || entry.person.trim()) && (
                  <IconButton
                    size="sm"
                    color="danger"
                    onClick={() => handleDelete(idx)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          ))}
          {loading ? (
            <CircularProgress />
          ) : (
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="solid" color="neutral" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={handleSubmit}
                disabled={!eventName.trim()}
              >
                Submit
              </Button>
            </Stack>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default AddEventModal;
